// Pure media-cache planning (docs/04 §Running / atomic swap).
//
// Separated from the Cache API side effects in `cache.ts` so the diff logic —
// the part that decides what to download and what to prune — is unit-testable
// without a browser.

import type { Manifest } from './types.ts';
import { overlayLogoUrl } from '@seencast/player-core';

/**
 * Every cacheable media URL referenced anywhere in a manifest (all timeline
 * windows + fallback + the overlay logo). The rule is MANIFEST_ITEM_DELIVERY
 * (@seencast/shared): type=url items are LIVE external pages rendered in an iframe
 * — never cached, so excluded here; EVERY other type ('image', 'video', 'html') is
 * cached, because its url is the device-token-authed /v1/player/media/:id which
 * only an authorized fetch can read. That is why generated html items (manifest
 * type 'html', both html_template and html_snippet) are downloaded here and play
 * offline like image/video. The overlay logo IS a cacheable media path, so it is
 * included: the manifest only swaps once its logo is cached, keeping the overlay
 * offline-safe and atomic with the swap. De-duplicated. When the manifest declares
 * a multi-zone layout (docs/04 §Zones), EVERY zone's media is unioned in too, so
 * the atomic swap only happens once all zones can play offline.
 */
export function mediaUrlsOf(manifest: Manifest): string[] {
  const urls = new Set<string>();
  const collect = (items: { type: string; url: string }[]) => {
    for (const it of items) {
      if (it.type !== 'url') urls.add(it.url);
    }
  };
  for (const w of manifest.timeline) collect(w.items);
  collect(manifest.fallback.items);
  // Each zone's `timeline` is itself a ManifestWindow[] — collect items per window.
  for (const z of manifest.zones ?? []) for (const w of z.timeline) collect(w.items);
  const logo = overlayLogoUrl(manifest.overlay);
  if (logo && !/^https?:/i.test(logo)) urls.add(logo);
  return [...urls];
}

export interface CachePlan {
  /** In `needed` but not yet cached — download these (sequentially). */
  toDownload: string[];
  /** Cached but no longer referenced by the active manifest — prune after swap. */
  toPrune: string[];
}

/**
 * Diff currently-cached URLs against the set a manifest needs.
 * `cached` and `needed` are compared as opaque strings (already normalised by
 * the caller). Order of `toDownload` follows `needed` so downloads are
 * deterministic and match manifest order.
 */
export function diffMedia(cached: Iterable<string>, needed: string[]): CachePlan {
  const cachedSet = new Set(cached);
  const neededSet = new Set(needed);
  const toDownload = needed.filter((u) => !cachedSet.has(u));
  const toPrune = [...cachedSet].filter((u) => !neededSet.has(u));
  return { toDownload, toPrune };
}

/** True when every media URL a manifest needs is already present in `cached`. */
export function isFullyCached(manifest: Manifest, cached: Iterable<string>): boolean {
  const cachedSet = new Set(cached);
  return mediaUrlsOf(manifest).every((u) => cachedSet.has(u));
}
